import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { Header } from "../components/header";
import { AppShell, Box, Flex, VisuallyHidden } from "@mantine/core";
import classes from "./root.module.css";
import { useEffect } from "react";
import {
  CustomerCredentials,
  useUpdateCustomerData,
} from "../hooks/ui/customerDataStore";
import { useShouldSetAppUI } from "../hooks/ui/useShouldSetAppUI";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { DeviceType, OperatingSystem } from "../types";
import { ApiMetricsPanel } from "../components/common/ApiMetricsPanel";

export interface RouterContext {
  queryClient: QueryClient;
  credentials: CustomerCredentials | undefined;
  browserInfo: {
    userAgent: string;
    isMobile: boolean;
    deviceType: DeviceType;
    os: OperatingSystem;
    isIOS: boolean;
    isAndroid: boolean;
    canUseBankIDApp: boolean;
    isTouch: boolean;
    touchPoints: number;
    screenWidth: number;
    screenHeight: number;
    language: string;
  };
}

const PAGE_TITLES: Record<string, string> = {
  "/login": "Logga in",
  "/home": "Hem",
  "/signup": "Bli kund",
  "/dashboard": "Översikt",
  "/pension-overview": "Din pension",
  "/pension-forecast": "Pensionsprognos",
  "/advice-summary": "Rådgivning",
  "/transfer-status": "Flyttstatus",
  "/transfer-terms": "Villkor",
  "/my-documents": "Dina dokument",
  "/profile": "Dina uppgifter",
  "/poa-renew": "Förnya fullmakt",
  "/open-sign-policy": "Signera flytt",
};

const getPageTitle = (pathname: string): string | undefined => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.endsWith("/holdings")) return "Innehav";
  if (pathname.endsWith("/transactions")) return "Transaktioner";
  if (pathname.endsWith("/information")) return "Försäkringsinformation";
  return undefined;
};

const Root = () => {
  const { updateCredentials, resetCustomerData } = useUpdateCustomerData();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const shouldSetAppUI = useShouldSetAppUI();
  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    document.title = pageTitle
      ? `${pageTitle} | Sambla Pension`
      : "Sambla Pension | Min Pension";
  }, [pageTitle]);

  // Let the layout CSS adapt to the native app chrome (header/back button)
  // only while the app-webview flow is active.
  useEffect(() => {
    document.body.classList.toggle("app-webview-mode", shouldSetAppUI);
    return () => document.body.classList.remove("app-webview-mode");
  }, [shouldSetAppUI]);

  useEffect(() => {
    if (shouldSetAppUI) return;

    const customerSession = sessionStorage.getItem("customerData");
    const credentials = customerSession ? JSON.parse(customerSession) : null;

    if (credentials) {
      const { expiration } = credentials;
      const currentTime = new Date().getTime();

      if (expiration && new Date(expiration).getTime() > currentTime) {
        updateCredentials(credentials);
      } else {
        sessionStorage.clear();
        resetCustomerData();
        queryClient.cancelQueries();
        queryClient.clear();
        navigate({
          to: "/login",
          search: window.location.search
            ? Object.fromEntries(new URLSearchParams(window.location.search))
            : undefined,
        });
      }
    } else {
      const hasNonce = window.location.hash.includes("nonce=");
      const allowWithoutCredentials =
        location.pathname === "/signup" ||
        ((location.pathname === "/open-sign-policy" ||
          location.pathname === "/poa-renew") &&
          hasNonce);

      if (!allowWithoutCredentials) {
        navigate({
          to: "/login",
          search: window.location.search
            ? Object.fromEntries(new URLSearchParams(window.location.search))
            : undefined,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <VisuallyHidden aria-live="polite" role="status">
        {pageTitle}
      </VisuallyHidden>
      {shouldSetAppUI ? (
        <Outlet />
      ) : (
        <AppShell header={{ height: 76 }} footer={{ height: 84 }}>
          <a href="#main-content" className={classes.skipLink}>
            Hoppa till innehållet
          </a>
          <Header />
          <AppShell.Main
            id="main-content"
            tabIndex={-1}
            className={classes.main}
            data-transfer-terms={location.pathname === "/transfer-terms"}
          >
            <Flex direction={"column"} align={"center"}>
              <Box maw={"770px"} w={"100%"} className={classes.box}>
                <Outlet />
              </Box>
            </Flex>
          </AppShell.Main>
          <ApiMetricsPanel />
        </AppShell>
      )}
    </>
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
});
